/**
 * Expert-review analytics constants. Call `trackEvent` at the click / mount site
 * (page or sheet), not in shared atomic UI (BackButton / PrimaryButton).
 * Pair with `EventNames` from misc.types. Stay within TrackEventPayload fields only.
 *
 * Field map (non-question):
 * - ScreenName → product surface (ExpertReviewScreen.*)
 * - CTA with a visible label → buttonLabel = that label
 * - Hub step / review edit row → buttonLabel = row title
 *
 * Question contract (no extra payload keys — TrackEventPayload only):
 * - eventId = EXPERT_REVIEW_QUESTION_${questionId}  ← join / attribute by question
 * - buttonLabel = questionId                         ← same id (display text is too long)
 * - scrollSection = stageId | preferenceTypeId
 * - responseName = selected optionId, or "id1,id2" for multi-select
 * - amount = numeric amount when the answer is an amount
 *
 * When to fire:
 * - ScreenLoaded → question becomes visible (identity only; no answer fields)
 * - UserClicked / ButtonTap → answer committed once:
 *   - SINGLE_SELECT → on option select (auto-advances)
 *   - SINGLE_SELECT_CONFIRM → on Confirm only (not on highlight tap)
 *   - AMOUNT → on Confirm (amount set)
 *   - MULTI_SELECT_GROUPS → on Next with full selection (not each toggle)
 *   - insight / other Next → on Next (responseName if an option answer exists)
 */

import { EventNames } from '../../../../types/misc.types'
import { trackEvent, type TrackEventPayload } from '../../../../utils/appBridge'

/** `payload.ScreenName` — expert-review surface. */
export const ExpertReviewScreen = {
  Hub: 'Expert review hub',
  StrategyQuestionnaire: 'Expert review strategy questionnaire',
  StrategyReview: 'Expert review strategy review',
  RiskProfile: 'Expert review risk profile',
  PreferencesList: 'Expert review preferences list',
  PreferencesQuestionnaire: 'Expert review preferences questionnaire',
  PreferencesReview: 'Expert review preferences review',
  Submit: 'Expert review submit'
} as const

/** `payload.bottomSheetName` / sheet open ids for BottomSheetLoad. */
export const ExpertReviewBottomSheet = {
  StrategyMenu: 'expert_review_strategy_menu',
  PreferencesMenu: 'expert_review_preferences_menu'
} as const

/** `payload.eventId` — which control on expert-review. */
export const ExpertReviewEventIds = {
  Back: 'EXPERT_REVIEW_BACK',
  Menu: 'EXPERT_REVIEW_MENU',
  MenuExit: 'EXPERT_REVIEW_MENU_EXIT',
  MenuGoBack: 'EXPERT_REVIEW_MENU_GO_BACK',
  HubStep: 'EXPERT_REVIEW_HUB_STEP',
  HubSubmit: 'EXPERT_REVIEW_HUB_SUBMIT',
  HubDone: 'EXPERT_REVIEW_HUB_DONE',
  IntroStart: 'EXPERT_REVIEW_INTRO_START',
  StrategyReviewEdit: 'EXPERT_REVIEW_STRATEGY_REVIEW_EDIT',
  StrategyReviewEditAll: 'EXPERT_REVIEW_STRATEGY_REVIEW_EDIT_ALL',
  FindMyStrategy: 'EXPERT_REVIEW_FIND_MY_STRATEGY',
  RiskContinue: 'EXPERT_REVIEW_RISK_CONTINUE',
  RiskReviewInputs: 'EXPERT_REVIEW_RISK_REVIEW_INPUTS',
  RiskRetry: 'EXPERT_REVIEW_RISK_RETRY',
  RiskGoBack: 'EXPERT_REVIEW_RISK_GO_BACK',
  PreferencesContinue: 'EXPERT_REVIEW_PREFERENCES_CONTINUE',
  PreferencesReviewEdit: 'EXPERT_REVIEW_PREFERENCES_REVIEW_EDIT',
  PreferencesReviewEditAll: 'EXPERT_REVIEW_PREFERENCES_REVIEW_EDIT_ALL',
  PreferencesSave: 'EXPERT_REVIEW_PREFERENCES_SAVE',
  Submit: 'EXPERT_REVIEW_SUBMIT'
} as const

/** `payload.eventId` for a question — includes questionId for funnel join. */
export function expertReviewQuestionEventId(questionId: string): string {
  return `EXPERT_REVIEW_QUESTION_${questionId}`
}

/** Serialize multi-select option ids for `responseName` (single payload string field). */
export function expertReviewOptionIdsResponseName(optionIds: string[]): string | undefined {
  if (optionIds.length === 0) return undefined
  return optionIds.join(',')
}

/**
 * Question view / committed answer. Always sets eventId + buttonLabel from questionId.
 * Pass only TrackEventPayload answer fields: scrollSection, responseName, amount.
 */
export function trackExpertReviewQuestionEvent({
  eventName,
  questionId,
  ScreenName,
  scrollSection,
  responseName,
  amount
}: {
  eventName: (typeof EventNames)[keyof typeof EventNames]
  questionId: string
  ScreenName: string
  scrollSection?: string
  responseName?: string
  amount?: number | string
}) {
  const payload: TrackEventPayload = {
    ScreenName,
    eventId: expertReviewQuestionEventId(questionId),
    buttonLabel: questionId
  }
  if (scrollSection) payload.scrollSection = scrollSection
  if (responseName) payload.responseName = responseName
  if (amount != null) payload.amount = amount

  trackEvent({ eventName, payload })
}
